import * as React from "react";
import {useContext, useEffect, useState} from "react";
import ReactTable from "react-table";
import {Dropdown, Icon, Input, Label, Popup, Segment} from "semantic-ui-react";
import {authentication} from "../../../../../../authentication";
import {formatDate} from "../../../../../../format";
import {backend} from "../../../../../../xconvert-backend";
import {ConfigContext} from "../../../../context/ConfigContext";
import {HistoryModal} from "../modals/HistoryModal";
import {RollbackConfigModal} from "../modals/RollbackConfigModal";
import {ChangeDetailModal} from "../modals/ChangeDetailModal";
import {workingConfig} from "../../ConfigSignal";
import {focusesOnField} from "../newNavTree/NavTree";
import {CompanyConfigurationChange} from "../../../../../../generated/api";

export function FieldChangesView() {
    const context = useContext(ConfigContext);
    const [isLoading, setIsLoading] = useState(false);
    const [fieldChanges, setFieldChanges] = useState<CompanyConfigurationChange[]>([]);
    const [showChanges, setShowChanges] = useState(false);
    const [history, setHistory] = useState(null);
    const [showRollBackView, setShowRollBackView] = useState(false);
    const [followingConfig, setFollowingConfig] = useState(null);
    const [take, setTake] = useState(50);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState<'TIMESTAMP' | 'CHANGED_BY' | 'COMMENT'>("TIMESTAMP");
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>("DESC");
    const [count, setCount] = useState(-1);
    const [selectedPathFilter, setSelectedPathFilter] = useState<string | null>(null);
    const [selectedUserFilter, setSelectedUserFilter] = useState<string | null>(null);
    const [commentFilter, setCommentFilter] = useState("");
    const [dateFromFilter, setDateFromFilter] = useState("");
    const [dateToFilter, setDateToFilter] = useState("");
    const [filteredChanges, setFilteredChanges] = useState<CompanyConfigurationChange[]>([]);
    const [selectedChange, setSelectedChange] = useState<CompanyConfigurationChange | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [isFetchingChanges, setIsFetchingChanges] = useState(false);
    const userClearedPathRef = React.useRef(false);


    useEffect(() => {
        console.log("[FieldChangesView] useEffect - something changed")

        fetchData().then();
    }, [take, page, sortBy, sortDirection, isLoading]);

    useEffect(() => {
        const next = (fieldChanges ?? []).filter(change => {
            const pathMatches = !selectedPathFilter || (change.path || "-") === selectedPathFilter;
            const userMatches = !selectedUserFilter || (change.userName || "-") === selectedUserFilter;
            const commentMatches = !commentFilter
                || (change.comment || "").toLowerCase().includes(commentFilter.toLowerCase().trim());
            const changeDate = change.timestamp ? new Date(change.timestamp as any) : null;
            const fromDate = dateFromFilter ? new Date(`${dateFromFilter}T00:00:00`) : null;
            const toDate = dateToFilter ? new Date(`${dateToFilter}T23:59:59.999`) : null;
            const hasValidTimestamp = !changeDate || !Number.isNaN(changeDate.getTime());
            const dateMatches = hasValidTimestamp && (
                (!fromDate || !changeDate || changeDate >= fromDate)
                && (!toDate || !changeDate || changeDate <= toDate)
            );

            return pathMatches && userMatches && commentMatches && dateMatches;
        });
        setFilteredChanges(next);
    }, [fieldChanges, selectedPathFilter, selectedUserFilter, commentFilter, dateFromFilter, dateToFilter]);

    function render() {
        function isPathTruncated(path: string, maxLen = 40): boolean {
            return !!path && path.length > maxLen;
        }

        function compactPath(path: string, maxLen = 40): string {
            if (!path || path.length <= maxLen) {
                return path;
            }
            const visible = Math.max(6, maxLen - 3);
            const startLen = Math.ceil(visible / 2);
            const endLen = Math.floor(visible / 2);
            return `${path.slice(0, startLen)}[..]${path.slice(-endLen)}`;
        }

        const selectedChangeTicketNrs: string[] = ((selectedChange as any)?.ticketNrs || [])
            .map((t: any) => String(t).trim())
            .filter(Boolean)
            .filter((v: string, i: number, arr: string[]) => arr.indexOf(v) === i);

        const basePathOptions = Array.from(new Set((fieldChanges ?? []).map(change => change.path || "-")))
            .sort((a, b) => a.localeCompare(b))
            .map(path => ({
                key: path,
                value: path,
                text: path,
                content: isPathTruncated(path)
                    ? (
                        <Popup
                            hoverable
                            position="top left"
                            trigger={<span>{compactPath(path)}</span>}
                            content={<pre style={{margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "520px"}}>{path}</pre>}
                        />
                    )
                    : <span>{path}</span>
            }));

        // If current filter value is not in the options, add it temporarily
        const pathOptions = selectedPathFilter && !basePathOptions.some(o => o.value === selectedPathFilter)
            ? [{
                key: selectedPathFilter,
                value: selectedPathFilter,
                text: `${selectedPathFilter} (no match)`,
                content: isPathTruncated(selectedPathFilter)
                    ? (
                        <Popup
                            hoverable
                            position="top left"
                            trigger={<span>{compactPath(selectedPathFilter)} (no match)</span>}
                            content={<pre style={{margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "520px"}}>{selectedPathFilter}</pre>}
                        />
                    )
                    : <span>{selectedPathFilter} (no match)</span>
            }, ...basePathOptions]
            : basePathOptions;

        const userOptions = Array.from(new Set((fieldChanges ?? []).map(change => change.userName || "-")))
            .sort((a, b) => a.localeCompare(b))
            .map(user => ({key: user, value: user, text: user}));

        const listRenderKey = `${selectedPathFilter ?? "all"}::${selectedUserFilter ?? "all"}::${commentFilter || "all"}::${dateFromFilter || "none"}::${dateToFilter || "none"}::${filteredChanges.length}`;
        const totalChanges = (fieldChanges ?? []).length;
        const shownChanges = (filteredChanges ?? []).length;

        const filterBar = <div
            style={{
                display: "flex",
                gap: "4px",
                marginBottom: "6px",
                flexWrap: "wrap",
                alignItems: "center",
                fontSize: "12px"
            }}
        >
            <Dropdown
                placeholder="Pfad"
                className="path-filter-dropdown"
                selection
                clearable
                search
                options={pathOptions}
                value={selectedPathFilter}
                onChange={(_, data) => {
                    const val = (data.value as string) || null;
                    if (!val) {
                        userClearedPathRef.current = true;
                        focusesOnField.value = null;
                    }
                    setSelectedPathFilter(val);
                }}
                style={{minWidth: "340px", maxWidth: "480px", fontSize: "12px"}}
            />
            <Dropdown
                placeholder="User"
                selection
                clearable
                search
                options={userOptions}
                value={selectedUserFilter}
                onChange={(_, data) => setSelectedUserFilter((data.value as string) || null)}
                style={{minWidth: "130px", fontSize: "12px"}}
            />
            <Input
                placeholder="Kommentar"
                icon="search"
                value={commentFilter}
                onChange={(_, data) => setCommentFilter((data.value as string) || "")}
                style={{minWidth: "150px", fontSize: "12px"}}
                size="small"
            />
            <Input
                type="date"
                value={dateFromFilter}
                onChange={(_, data) => setDateFromFilter((data.value as string) || "")}
                style={{fontSize: "12px"}}
                title="Von"
                size="small"
            />
            <span style={{color: "#94a3b8", fontSize: "11px", padding: "0 2px"}}>–</span>
            <Input
                type="date"
                value={dateToFilter}
                onChange={(_, data) => setDateToFilter((data.value as string) || "")}
                style={{fontSize: "12px"}}
                title="Bis"
                size="small"
            />
            <span style={{marginLeft: "6px", color: "#64748b", fontSize: "11px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"}}>
                {shownChanges}/{totalChanges}
            </span>
        </div>;

        if (isFetchingChanges) {
            return <Segment loading>
                {filterBar}
                <Label color="blue">Loading changes...</Label>
            </Segment>
        }

        if (fieldChanges == null || fieldChanges.length == 0) {
            return <Segment>
                {filterBar}
                <Label color="yellow">No change found</Label>
            </Segment>
        }

        return <Segment loading={isFetchingChanges}>
            {filterBar}
            <div key={listRenderKey}>
                {drawElementsList(filteredChanges)}
            </div>

            <HistoryModal
                isOpen={showChanges}
                onClose={(openRollback: boolean) => {
                    if (openRollback) {
                        setShowChanges(false)
                        setShowRollBackView(true)
                    } else {
                        setShowChanges(false)
                    }
                }}
                history={history}
                followingConfig={followingConfig}
            />
            <RollbackConfigModal
                isOpen={showRollBackView}
                onClose={() => setShowRollBackView(false)}
                currentConfig={workingConfig.value}
                history={history}
            />
            <ChangeDetailModal
                isOpen={showDetailModal}
                onClose={() => setShowDetailModal(false)}
                change={selectedChange}
                ticketNrs={selectedChangeTicketNrs}
                onOpenHistory={(historyId: string) => {
                    setShowDetailModal(false);
                    displayChanges(historyId).then();
                }}
            />

        </Segment>
    }

    function drawTable() {
        const
            columns = [
                {
                    id: 'TIMESTAMP',
                    Header: 'timestamp',
                    width: 150,
                    accessor: (d: any) => d.timestamp,
                    Cell: (d: any) => {
                        return <span>{formatDate(d.value)}</span>
                    },
                    sortMethod: (a, b) => {
                        const a1 = new Date(a).getTime();
                        const b1 = new Date(b).getTime();
                        if (a1 < b1) {
                            return 1;
                        } else if (a1 > b1) {
                            return -1;
                        } else {
                            return 0;
                        }
                    },
                    sortField: 'TIMESTAMP'

                }, {
                    id: 'CHANGED_BY',
                    Header: 'changedBy',
                    width: 200,
                    accessor: (d: any) => d.userName,
                    sortField: 'CHANGED_BY'
                }, {
                    id: 'COMMENT',
                    Header: 'comment',
                    accessor: (d: any) => d.comment,
                    sortField: 'COMMENT'
                }]

        if (fieldChanges == null) {
            return <p>No entries found</p>
        } else {

            return <ReactTable
                data={fieldChanges}
                pages={Math.ceil(count / take)}
                columns={columns}
                sorted={[
                    {
                        id: 'TIMESTAMP',
                        desc: false
                    }
                ]}
                onSortedChange={(_newSorted, column) => {
                    changeSort(column.sortField).then(r => {
                    })
                }}

                defaultPageSize={take}
                className="-striped -highlight"
                loading={isLoading}
                getTdProps={(_: any, rowInfo: { row: { _original: { historyId: string; }; }; }) => {
                    return {
                        onClick: () => {
                            displayChanges(rowInfo.row._original.historyId).then(r => {
                            })
                        }
                    };
                }}
                style={{cursor: "pointer"}}
                onPageChange={(pageIndex) => changePage(pageIndex)}
                onPageSizeChange={(pageSize, pageIndex) => changePageSize(pageSize)}

            />

        }
    }


    function drawElementsList(changes: CompanyConfigurationChange[]) {

        function extractTicketNrsFromHistory(change: CompanyConfigurationChange): string[] {
            return ((change as any).ticketNrs || [])
                .map((t: any) => String(t).trim())
                .filter(Boolean)
                .filter((v: string, i: number, arr: string[]) => arr.indexOf(v) === i);
        }

        function rawJson(change: CompanyConfigurationChange): string {
            try {
                return JSON.stringify(change, null, 2);
            } catch {
                return "Could not serialize change object";
            }
        }

        function normalizeValue(value: any): string {
            if (value === null || value === undefined) {
                return "-";
            }
            if (typeof value === "string") {
                return value;
            }
            if (typeof value === "object") {
                try {
                    return JSON.stringify(value, null, 2);
                } catch {
                    return String(value);
                }
            }
            return String(value);
        }

        type DiffSegment = { text: string; changed: boolean };

        function linesToSegments(lines: { text: string; changed: boolean }[]): DiffSegment[] {
            if (lines.length === 0) {
                return [{text: "", changed: false}];
            }
            const segments: DiffSegment[] = [];
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                const text = i < lines.length - 1 ? `${line.text}\n` : line.text;
                const previous = segments[segments.length - 1];
                if (!previous || previous.changed !== line.changed) {
                    segments.push({text, changed: line.changed});
                } else {
                    previous.text += text;
                }
            }
            return segments;
        }

        function buildDiffSegments(oldValue: any, newValue: any): { oldSegments: DiffSegment[]; newSegments: DiffSegment[] } {
            const oldText = normalizeValue(oldValue);
            const newText = normalizeValue(newValue);

            if (oldText === newText) {
                return {
                    oldSegments: [{text: oldText, changed: false}],
                    newSegments: [{text: newText, changed: false}]
                };
            }

            const oldLinesRaw = oldText.split("\n");
            const newLinesRaw = newText.split("\n");
            const n = oldLinesRaw.length;
            const m = newLinesRaw.length;

            const dp: number[][] = Array.from({length: n + 1}, () => Array(m + 1).fill(0));
            for (let i = n - 1; i >= 0; i--) {
                for (let j = m - 1; j >= 0; j--) {
                    if (oldLinesRaw[i] === newLinesRaw[j]) {
                        dp[i][j] = dp[i + 1][j + 1] + 1;
                    } else {
                        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
                    }
                }
            }

            const oldLines: { text: string; changed: boolean }[] = [];
            const newLines: { text: string; changed: boolean }[] = [];

            let i = 0;
            let j = 0;
            while (i < n && j < m) {
                if (oldLinesRaw[i] === newLinesRaw[j]) {
                    oldLines.push({text: oldLinesRaw[i], changed: false});
                    newLines.push({text: newLinesRaw[j], changed: false});
                    i++;
                    j++;
                    continue;
                }
                if (dp[i + 1][j] >= dp[i][j + 1]) {
                    oldLines.push({text: oldLinesRaw[i], changed: true});
                    i++;
                } else {
                    newLines.push({text: newLinesRaw[j], changed: true});
                    j++;
                }
            }
            while (i < n) {
                oldLines.push({text: oldLinesRaw[i], changed: true});
                i++;
            }
            while (j < m) {
                newLines.push({text: newLinesRaw[j], changed: true});
                j++;
            }

            return {
                oldSegments: linesToSegments(oldLines),
                newSegments: linesToSegments(newLines)
            };
        }

        function renderDiffText(segments: DiffSegment[], isGreen: boolean) {
            const highlightStyle = {
                backgroundColor: isGreen ? "#bbf7d0" : "#fecdd3",
                borderRadius: "4px",
                padding: "0 2px"
            };
            return <span style={{whiteSpace: "pre-wrap"}}>
                {segments.map((segment, index) => (
                    <span key={`seg-${index}`} style={segment.changed ? highlightStyle : undefined}>{segment.text}</span>
                ))}
            </span>;
        }

        function valueBox(segments: DiffSegment[], color: "green" | "red") {
            const isGreen = color === "green";
            const bg = isGreen ? "#edfdf3" : "#fff1f2";
            const border = isGreen ? "#16a34a" : "#e11d48";
            const text = isGreen ? "#14532d" : "#881337";
            const maxChars = 90;

            function sliceSegments(input: DiffSegment[], start: number, end: number): DiffSegment[] {
                if (end <= start) {
                    return [];
                }
                const result: DiffSegment[] = [];
                let cursor = 0;
                input.forEach((segment) => {
                    const segStart = cursor;
                    const segEnd = cursor + segment.text.length;
                    cursor = segEnd;

                    const overlapStart = Math.max(start, segStart);
                    const overlapEnd = Math.min(end, segEnd);
                    if (overlapEnd <= overlapStart) {
                        return;
                    }
                    const textPart = segment.text.slice(overlapStart - segStart, overlapEnd - segStart);
                    const previous = result[result.length - 1];
                    if (previous && previous.changed === segment.changed) {
                        previous.text += textPart;
                    } else {
                        result.push({text: textPart, changed: segment.changed});
                    }
                });
                return result;
            }

            function truncateSegments(input: DiffSegment[], max: number): { segments: DiffSegment[]; truncated: boolean } {
                const fullText = input.map((s) => s.text).join("");
                if (fullText.length <= max) {
                    return {segments: input, truncated: false};
                }

                let cursor = 0;
                let firstChangedStart = -1;
                let lastChangedEnd = -1;
                input.forEach((segment) => {
                    const start = cursor;
                    const end = cursor + segment.text.length;
                    if (segment.changed && segment.text.length > 0) {
                        if (firstChangedStart < 0) {
                            firstChangedStart = start;
                        }
                        lastChangedEnd = end;
                    }
                    cursor = end;
                });

                if (firstChangedStart < 0) {
                    return {
                        segments: [{text: `${fullText.slice(0, Math.max(0, max - 3))}...`, changed: false}],
                        truncated: true
                    };
                }

                const changedLen = Math.max(1, lastChangedEnd - firstChangedStart);
                let windowStart: number;
                let windowEnd: number;

                if (changedLen >= max - 3) {
                    windowStart = firstChangedStart;
                    windowEnd = Math.min(fullText.length, firstChangedStart + (max - 3));
                } else {
                    const remaining = max - changedLen;
                    let left = Math.floor(remaining / 2);
                    let right = remaining - left;
                    windowStart = Math.max(0, firstChangedStart - left);
                    windowEnd = Math.min(fullText.length, lastChangedEnd + right);

                    const currentLen = windowEnd - windowStart;
                    if (currentLen < max) {
                        const missing = max - currentLen;
                        const growLeft = Math.min(missing, windowStart);
                        windowStart -= growLeft;
                        windowEnd = Math.min(fullText.length, windowEnd + (missing - growLeft));
                    }
                }

                const result = sliceSegments(input, windowStart, windowEnd);
                if (windowStart > 0) {
                    result.unshift({text: "...", changed: false});
                }
                if (windowEnd < fullText.length) {
                    result.push({text: "...", changed: false});
                }
                return {segments: result, truncated: true};
            }

            const truncatedDiff = truncateSegments(segments, maxChars);

            return <div
                style={{
                    padding: "4px 6px",
                    backgroundColor: bg,
                    color: text,
                    border: `1px solid ${border}`,
                    borderRadius: "6px",
                    textAlign: "left",
                    fontWeight: 500,
                    fontSize: "11px",
                    minWidth: "95px",
                    maxWidth: "220px",
                    boxShadow: "0 1px 1px rgba(15, 23, 42, 0.05)",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    lineHeight: 1.25
                }}>
                {renderDiffText(truncatedDiff.segments, isGreen)}
                {truncatedDiff.truncated ? <span style={{opacity: 0.85}}> ... more</span> : null}
            </div>
        }

        return <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px"
            }}
        >
            {changes.map((change) => {
                const diffSegments = buildDiffSegments(change.oldValue, change.value);
                const ticketNrs = extractTicketNrsFromHistory(change);
                const segmentKey = [
                    change.historyId || "history",
                    change.path || "path",
                    String(change.timestamp || "ts"),
                    change.userName || "user",
                    String(change.comment || "comment")
                ].join("::");
                return <Segment
                    key={segmentKey}
                    onClick={() => {
                        setSelectedChange(change);
                        setShowDetailModal(true);
                    }}
                    style={{
                        margin: 0,
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
                        padding: "8px 10px",
                        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                        cursor: "pointer",
                        transition: "box-shadow 0.15s, border-color 0.15s"
                    }}
                    onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 16px rgba(15, 23, 42, 0.12)";
                        (e.currentTarget as HTMLElement).style.borderColor = "#94a3b8";
                    }}
                    onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.boxShadow = "0 2px 8px rgba(15, 23, 42, 0.05)";
                        (e.currentTarget as HTMLElement).style.borderColor = "#e2e8f0";
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px"
                        }}
                    >
                        <span
                            style={{
                                fontSize: "10px",
                                color: "#64748b",
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                width: "100%"
                            }}
                        >
                            {change.path || "-"}
                        </span>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                gap: "10px",
                                alignItems: "stretch",
                                flexWrap: "wrap"
                            }}
                        >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                minWidth: "150px",
                                gap: "2px"
                            }}
                        >
                            <span style={{fontSize: "14px", fontWeight: 700, color: "#0f172a"}}>{change.userName}</span>
                            <span style={{fontSize: "11px", color: "#475569"}}>{formatDate(change.timestamp)}</span>
                            {ticketNrs.length > 0 ? (
                                <div style={{fontSize: "11px", marginTop: "2px", display: "flex", flexWrap: "wrap", gap: "4px"}}>
                                    {ticketNrs.map((ticketNr, index) => {
                                        const ticketUrl = `https://gitlab.logenios.com/LOGENIOS/xconvert/-/work_items/${ticketNr}`;
                                        return (
                                            <React.Fragment key={`${change.historyId || "history"}-${ticketNr}`}>
                                                <a
                                                    href={ticketUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(event) => event.stopPropagation()}
                                                    style={{
                                                        color: "#2563eb",
                                                        textDecoration: "none",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "4px"
                                                    }}
                                                >
                                                    #{ticketNr}
                                                    <Icon name="external" style={{margin: 0, fontSize: "10px"}}/>
                                                </a>
                                                {index < ticketNrs.length - 1 ? <span style={{color: "#94a3b8"}}>,</span> : null}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                flex: 1,
                                minWidth: "180px"
                            }}
                        >
                            <span style={{fontSize: "10px", color: "#64748b", letterSpacing: "0.04em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "4px"}}>
                                Comment
                                <Popup
                                    hoverable
                                    wide="very"
                                    trigger={<Icon name="info circle" style={{cursor: "help", color: "#64748b"}}/>}
                                    content={
                                        <pre style={{margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "min(520px, calc(100vw - 48px))", overflow: "visible"}}>
                                            {rawJson(change)}
                                        </pre>
                                    }
                                />
                            </span>
                            <span style={{fontSize: "12px", color: "#0f172a", marginTop: "2px"}}>{change.comment || "-"}</span>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "6px",
                                marginLeft: "auto"
                            }}
                        >
                            {valueBox(diffSegments.oldSegments, "red")}
                            <Icon name={"arrow right"} style={{alignSelf: "center", color: "#64748b", margin: 0}}/>
                            {valueBox(diffSegments.newSegments, "green")}
                        </div>
                        </div>
                    </div>
                </Segment>
            })}
        </div>
    }

    async function fetchData() {
        setIsFetchingChanges(true)
        try {
            let auth = backend.withTokenAuthHeader(authentication.token)
            let response = await backend.internalApi.fetchCompanyConfigurationChanges(context.companyId, null, auth)

            const sortedChanges = [...(response.changes || [])].sort((a, b) => {
                const left = sortBy === 'TIMESTAMP' ? (a.timestamp || '') : sortBy === 'CHANGED_BY' ? (a.userName || '') : (a.comment || '')
                const right = sortBy === 'TIMESTAMP' ? (b.timestamp || '') : sortBy === 'CHANGED_BY' ? (b.userName || '') : (b.comment || '')

                if (sortBy === 'TIMESTAMP') {
                    const leftTs = new Date(left).getTime()
                    const rightTs = new Date(right).getTime()
                    return sortDirection === 'ASC' ? leftTs - rightTs : rightTs - leftTs
                }

                const cmp = String(left).localeCompare(String(right))
                return sortDirection === 'ASC' ? cmp : -cmp
            })

            setFieldChanges(sortedChanges)
            setCount(sortedChanges.length)
            setTake(take)
            setPage(page)
            setIsLoading(false)
        } finally {
            setIsFetchingChanges(false)
        }
    }


    async function displayChanges(historyId: string) {

        let auth = backend.withTokenAuthHeader(authentication.token)
        let response = await backend.internalApi.fetchFollowingCompanyConfigurationHistory(context.companyId, historyId, auth)
        let followingConfig = response.followingConfig
        let history = response.historyEntry
        setShowChanges(true)
        setHistory(history)
        setFollowingConfig(followingConfig)
    }

    async function changeSort(sortBy: 'TIMESTAMP' | 'CHANGED_BY' | 'COMMENT' | 'NONE') {
        console.log("changing sort")
        setIsLoading(true)
        if (sortBy == 'NONE') {
            return
        }

        if (sortBy == sortBy) {
            //change direction
            let direction = sortDirection
            if (direction == "ASC") {
                direction = "DESC"
            } else {
                direction = "ASC"
            }
            setSortDirection(direction)
        } else {
            //change sortBy and reset direction
            setSortBy(sortBy)
            setSortDirection("ASC")
        }

        setIsLoading(false)
    }

    function changePage(pageIndex: number) {
        setIsLoading(true)
        setPage(pageIndex + 1)
    }

    function changePageSize(newSize: number) {
        setIsLoading(true)
        setTake(newSize)
    }

    return render();
}